import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { SERVICES } from "../lib/siteData";
import CTABanner from "../components/CTABanner";
import SEO from "../components/SEO";

export default function ProductPage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" />;

  const Icon = service.icon;

  return (
    <div data-testid={`product-page-${slug}`}>
      <SEO title={service.title} description={service.summary} />
      <section
        className="relative py-20 lg:py-28 text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.75), rgba(29,78,216,0.55)), url(${service.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container-narrow">
          <div className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-600/40 text-blue-300 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-5">
            <Icon className="w-3.5 h-3.5" /> Products & Services
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-4 max-w-3xl">{service.title}</h1>
          <p className="text-lg text-slate-200 max-w-2xl">{service.summary}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary" data-testid="product-get-quote">Get Quote <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/calculator" className="btn-outline bg-white/10 text-white border-white/30 hover:bg-white/20" data-testid="product-calculator">Estimate Savings</Link>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-narrow grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="badge-eco mb-3">Overview</div>
              <h2 className="text-3xl font-heading font-semibold text-slate-900 mb-4">Why {service.title} from CNGTPL Solar</h2>
              {service.details && (
                <p className="text-slate-600 leading-relaxed mb-5 text-[15px]">{service.details}</p>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                {service.benefits.map((b) => (
                  <div key={b} className="flex gap-2 items-start bg-white rounded-xl border border-slate-200 p-4">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700 font-medium">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {service.specs && (
              <div className="rounded-2xl bg-slate-900 text-white p-6 border-l-4 border-yellow-400">
                <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-400" /> Technical Specifications
                </h3>
                <ul className="space-y-2 text-sm text-slate-300">
                  {service.specs.map((s) => (
                    <li key={s} className="flex gap-2"><span className="text-yellow-400 mt-0.5">▸</span> {s}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-6">
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">What's included</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                {[
                  "Tier-1 modules with 25-year performance warranty",
                  "MNRE-approved inverter with 5-10 year warranty",
                  "Robust aluminium mounting structure (10-year warranty)",
                  "DC & AC cabling + protection devices (DCDB, ACDB, SPDs)",
                  "Net-metering & subsidy paperwork (residential)",
                  "Commissioning, testing & handover training",
                ].map((i) => (
                  <li key={i} className="flex gap-2"><Sparkles className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" /> {i}</li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 text-white p-6 shadow-xl">
              <div className="text-xs uppercase tracking-widest text-blue-100 font-bold mb-1">Starting at</div>
              <div className="stat-number text-4xl font-bold mb-1">{service.pricing}</div>
              <div className="text-sm text-blue-50 mb-5">*Exact pricing depends on roof survey, brand & capacity.</div>
              <Link to="/contact" className="block text-center font-semibold py-3 px-5 rounded-lg bg-white text-blue-800 hover:bg-slate-50 transition" data-testid="product-sidebar-cta">
                Request Detailed Quote
              </Link>
            </div>

            <div className="rounded-2xl border border-slate-200 p-6">
              <h4 className="font-heading font-semibold text-slate-900 mb-3">Other services</h4>
              <ul className="space-y-2 text-sm">
                {SERVICES.filter((s) => s.slug !== slug).map((s) => (
                  <li key={s.slug}>
                    <Link to={`/products/${s.slug}`} className="flex items-center justify-between text-slate-700 hover:text-blue-700 transition py-1.5">
                      <span>{s.title}</span> <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <CTABanner title={`Ready to install ${service.title}?`} subtitle="Free site survey. Transparent pricing. Hassle-free installation." />
    </div>
  );
}
